import {
  Dialog, DialogContent, DialogActions, Button, Stack, Typography, Chip, Box,
  Table, TableHead, TableRow, TableCell, TableBody, LinearProgress, Grid, Avatar, IconButton, Tooltip,
} from '@mui/material';
import {
  EvStation, Close, LocationOn, Groups, ElectricalServices, Description, Bolt, Info,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { amStationsApi } from '../../api/amStations.api';

const inr = (v: number | string | null | undefined) =>
  v == null || v === '' ? '—' : `₹${Number(v).toLocaleString('en-IN')}`;

const fmtDate = (v: React.ReactNode) => {
  if (v == null || v === '') return '—';
  const d = new Date(String(v));
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/** A single label→value tile with a subtle surface. */
const F = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box sx={{ px: 1.5, py: 1, borderRadius: 1.5, bgcolor: 'action.hover', height: '100%' }}>
    <Typography sx={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', color: 'text.secondary' }}>{label}</Typography>
    <Typography variant="body2" fontWeight={600} sx={{ mt: 0.25, wordBreak: 'break-word' }}>{value ?? '—'}</Typography>
  </Box>
);

/** A section wrapper: icon + accent title, then content in a card. */
const Section = ({ icon, title, color, children }: { icon: React.ReactNode; title: React.ReactNode; color: string; children: React.ReactNode }) => (
  <Box>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
      <Box sx={{ width: 28, height: 28, borderRadius: 1, display: 'grid', placeItems: 'center', bgcolor: color, color: '#fff' }}>{icon}</Box>
      <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
    </Stack>
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5, bgcolor: 'background.paper' }}>{children}</Box>
  </Box>
);

const headCellSx = { fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.3, color: 'text.secondary', bgcolor: 'action.hover', borderBottom: 0 } as const;

export function StationDetailDialog({ stationId, onClose }: { stationId: number | null; onClose: () => void }) {
  const { data: s, isLoading } = useQuery({ queryKey: ['am-station', stationId], queryFn: () => amStationsApi.get(stationId!), enabled: stationId != null });
  const totalCapacity = (s?.chargers ?? []).reduce((a, c) => a + Number(c.capacityKw || 0), 0);
  const active = s?.status === 'active';

  return (
    <Dialog open={stationId != null} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      {/* Header banner */}
      <Box sx={{ px: 3, py: 2.5, background: 'linear-gradient(135deg, #1565c0 0%, #2e7d32 100%)', color: '#fff' }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.18)', width: 48, height: 48 }}><EvStation /></Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 600, letterSpacing: 0.5 }}>{s?.stationCode ?? 'STATION'}</Typography>
            <Typography variant="h6" fontWeight={800} lineHeight={1.15} noWrap>{s?.stationName ?? 'Station'}</Typography>
            {s && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                <Chip size="small" label={s.status} sx={{ bgcolor: active ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.25)', color: active ? '#1b5e20' : '#fff', fontWeight: 700, textTransform: 'capitalize' }} />
                <Chip size="small" variant="outlined" label={`Owner: ${s.ownerType}`} sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }} />
                {s.clientName && <Chip size="small" variant="outlined" label={s.clientName} sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }} />}
              </Stack>
            )}
          </Box>
          <Tooltip title="Close"><IconButton onClick={onClose} sx={{ color: '#fff' }}><Close /></IconButton></Tooltip>
        </Stack>
      </Box>

      <DialogContent sx={{ bgcolor: 'grey.50', p: 2.5 }}>
        {isLoading && <LinearProgress />}
        {s && (
          <Stack spacing={2.5}>
            <Section icon={<Info fontSize="small" />} title="Site details" color="#455a64">
              <Grid container spacing={1.25}>
                <Grid item xs={6} sm={4}><F label="City / State" value={[s.city, s.state].filter(Boolean).join(', ') || '—'} /></Grid>
                <Grid item xs={6} sm={4}><F label="Pincode" value={s.pincode} /></Grid>
                <Grid item xs={6} sm={4}><F label="Commissioned" value={fmtDate(s.commissioningDate)} /></Grid>
                <Grid item xs={6} sm={4}><F label="Property" value={s.propertyType} /></Grid>
                <Grid item xs={6} sm={4}><F label="Doc status" value={s.propertyDocStatus} /></Grid>
                <Grid item xs={6} sm={4}><F label="Parking" value={s.parkingType} /></Grid>
                <Grid item xs={6} sm={4}><F label="Site area (sq ft)" value={s.siteAreaSqft} /></Grid>
                <Grid item xs={6} sm={4}><F label="Ops manager" value={s.assignedOpsManager} /></Grid>
                {s.companyGstin && <Grid item xs={6} sm={4}><F label="GSTIN" value={s.companyGstin} /></Grid>}
                {s.companyGstCertificateUrl && <Grid item xs={6} sm={4}><F label="GST certificate" value={<a href={s.companyGstCertificateUrl} target="_blank" rel="noreferrer">View / download</a>} /></Grid>}
                {(s.latitude != null && s.longitude != null) && <Grid item xs={6} sm={4}><F label="GPS" value={<a href={`https://www.google.com/maps?q=${s.latitude},${s.longitude}`} target="_blank" rel="noreferrer">{s.latitude}, {s.longitude}</a>} /></Grid>}
                {s.propertyLink && <Grid item xs={6} sm={4}><F label="Property link" value={<a href={s.propertyLink} target="_blank" rel="noreferrer">Open</a>} /></Grid>}
                {s.address && <Grid item xs={12} sm={8}><F label="Address" value={<span><LocationOn sx={{ fontSize: 14, verticalAlign: -2, mr: 0.5, color: 'error.main' }} />{s.address}</span>} /></Grid>}
              </Grid>
              {s.sitePhotos?.length > 0 && (
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                  {s.sitePhotos.map((p, i) => (
                    <a key={i} href={p} target="_blank" rel="noreferrer">
                      <Box component="img" src={p} alt={`Site photo ${i + 1}`} sx={{ width: 96, height: 72, objectFit: 'cover', borderRadius: 1.5, border: 1, borderColor: 'divider' }} />
                    </a>
                  ))}
                </Stack>
              )}
            </Section>

            <Section icon={<Groups fontSize="small" />} title={`Owners (${s.owners.length})`} color="#6a1b9a">
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell sx={headCellSx}>Owner</TableCell><TableCell sx={headCellSx}>Phone</TableCell>
                  <TableCell sx={headCellSx}>Bank / UPI</TableCell><TableCell sx={headCellSx} align="right">Share %</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {s.owners.map((o) => (
                    <TableRow key={o.id} hover>
                      <TableCell>{o.ownerName}{o.isPrimary ? <Chip size="small" color="primary" label="Primary" sx={{ ml: 1, height: 18, fontSize: 10 }} /> : null}</TableCell>
                      <TableCell>{o.ownerPhone ?? '—'}</TableCell>
                      <TableCell>
                        {o.bankName && <Typography variant="body2" fontWeight={600}>{o.bankName}</Typography>}
                        <Typography variant="body2" color={o.bankName ? 'text.secondary' : 'text.primary'}>{o.bankAccount ?? o.upiId ?? '—'}{o.ifscCode ? ` · ${o.ifscCode}` : ''}</Typography>
                      </TableCell>
                      <TableCell align="right"><Chip size="small" variant="outlined" label={`${o.rentSharePercent ?? 100}%`} /></TableCell>
                    </TableRow>
                  ))}
                  {s.owners.length === 0 && <TableRow><TableCell colSpan={4}><Typography variant="caption" color="text.secondary">No owners recorded.</Typography></TableCell></TableRow>}
                </TableBody>
              </Table>
            </Section>

            <Section icon={<Bolt fontSize="small" />} title={<span>Chargers ({s.chargers.length}) · <b>{totalCapacity} kW</b> total</span>} color="#ef6c00">
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell sx={headCellSx}>#</TableCell><TableCell sx={headCellSx}>Type</TableCell><TableCell sx={headCellSx}>Capacity</TableCell>
                  <TableCell sx={headCellSx}>Make / Model</TableCell><TableCell sx={headCellSx}>Serial</TableCell><TableCell sx={headCellSx}>Status</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {s.chargers.map((c, i) => (
                    <TableRow key={c.id} hover>
                      <TableCell>{c.chargerIndex ?? i + 1}</TableCell>
                      <TableCell>{c.chargerType ? <Chip size="small" label={c.chargerType} variant="outlined" /> : '—'}</TableCell>
                      <TableCell>{c.capacityKw != null ? `${c.capacityKw} kW` : '—'}</TableCell>
                      <TableCell>{[c.make, c.modelNumber].filter(Boolean).join(' ') || '—'}</TableCell>
                      <TableCell>{c.serialNumber ?? '—'}</TableCell>
                      <TableCell><Chip size="small" label={c.status ?? 'active'} color={c.status === 'faulty' || c.status === 'removed' ? 'error' : c.status === 'inactive' ? 'default' : 'success'} sx={{ textTransform: 'capitalize' }} /></TableCell>
                    </TableRow>
                  ))}
                  {s.chargers.length === 0 && <TableRow><TableCell colSpan={6}><Typography variant="caption" color="text.secondary">No chargers recorded.</Typography></TableCell></TableRow>}
                </TableBody>
              </Table>
            </Section>

            <Section icon={<Description fontSize="small" />} title="Rent & Agreement" color="#2e7d32">
              {s.rentAgreement ? (
                <Grid container spacing={1.25}>
                  <Grid item xs={6} sm={4}><F label="Agreement #" value={s.rentAgreement.agreementNumber} /></Grid>
                  <Grid item xs={6} sm={4}><F label="Monthly rent" value={inr(s.rentAgreement.monthlyRentAmount)} /></Grid>
                  <Grid item xs={6} sm={4}><F label="Escalation" value={s.rentAgreement.escalationPercent != null ? `${s.rentAgreement.escalationPercent}%` : '—'} /></Grid>
                  <Grid item xs={6} sm={4}><F label="Security deposit" value={inr(s.rentAgreement.securityDepositAmount)} /></Grid>
                  <Grid item xs={6} sm={4}><F label="Term" value={`${fmtDate(s.rentAgreement.agreementStartDate)} → ${fmtDate(s.rentAgreement.agreementEndDate)}`} /></Grid>
                  <Grid item xs={6} sm={4}><F label="Auto-renewal" value={s.rentAgreement.autoRenewal ? 'Yes' : 'No'} /></Grid>
                  <Grid item xs={6} sm={4}><F label="Rent due day" value={s.rentAgreement.rentDueDay} /></Grid>
                  <Grid item xs={6} sm={4}><F label="Notice period" value={s.rentAgreement.noticePeriodDays != null ? `${s.rentAgreement.noticePeriodDays} days` : '—'} /></Grid>
                  <Grid item xs={6} sm={4}><F label="Stamp duty" value={inr(s.rentAgreement.stampDutyAmount)} /></Grid>
                </Grid>
              ) : <Typography variant="body2" color="text.secondary">Not added yet.</Typography>}
            </Section>

            <Section icon={<ElectricalServices fontSize="small" />} title="Electricity / DISCOM" color="#0277bd">
              {s.electricityConnection ? (
                <Grid container spacing={1.25}>
                  <Grid item xs={6} sm={4}><F label="DISCOM" value={s.electricityConnection.discomName} /></Grid>
                  <Grid item xs={6} sm={4}><F label="Consumer #" value={s.electricityConnection.consumerNumber} /></Grid>
                  <Grid item xs={6} sm={4}><F label="Connection" value={s.electricityConnection.connectionType} /></Grid>
                  <Grid item xs={6} sm={4}><F label="Sanctioned load" value={s.electricityConnection.sanctionedLoadKw != null ? `${s.electricityConnection.sanctionedLoadKw} kW` : '—'} /></Grid>
                  {s.electricityConnection.connectionType === 'HT' && <Grid item xs={6} sm={4}><F label="Contract demand" value={s.electricityConnection.contractDemandKva != null ? `${s.electricityConnection.contractDemandKva} kVA` : '—'} /></Grid>}
                  <Grid item xs={6} sm={4}><F label="Tariff" value={s.electricityConnection.tariffCategory} /></Grid>
                  <Grid item xs={6} sm={4}><F label="Meter" value={[s.electricityConnection.meterNumber, s.electricityConnection.meterType].filter(Boolean).join(' · ') || '—'} /></Grid>
                  <Grid item xs={6} sm={4}><F label="Bill due day" value={s.electricityConnection.billDueDay} /></Grid>
                  <Grid item xs={6} sm={4}><F label="Avg units" value={s.electricityConnection.averageMonthlyUnitsKwh != null ? `${s.electricityConnection.averageMonthlyUnitsKwh} kWh` : '—'} /></Grid>
                  <Grid item xs={12}><Typography sx={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', color: 'text.secondary', mt: 0.5 }}>Portal access (masked)</Typography></Grid>
                  <Grid item xs={6} sm={4}><F label="Reg. mobile" value={s.electricityConnection.discomRegisteredMobileMasked} /></Grid>
                  <Grid item xs={6} sm={4}><F label="Reg. email" value={s.electricityConnection.discomRegisteredEmailMasked} /></Grid>
                  <Grid item xs={6} sm={4}><F label="Portal login" value={s.electricityConnection.discomPortalLoginIdMasked} /></Grid>
                </Grid>
              ) : <Typography variant="body2" color="text.secondary">Not added yet.</Typography>}
            </Section>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5, bgcolor: 'grey.50', borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} variant="contained">Close</Button>
      </DialogActions>
    </Dialog>
  );
}
